import { useEffect, useCallback } from 'react';
import { useStore, State } from 'store';

import fetchWorkflowProgress from 'store/fetchWorkflowProgress';
import getCurrentProjectState from 'store/getCurrentProjectState';

import IAsync from 'types/IAsync';
import IWorkflowProgress from 'types/IWorkflowProgress';
import RunState from 'types/RunState';

const initAsync: IAsync<IWorkflowProgress> = { status: 'INIT' };

const TERMINAL_STATES: ReadonlySet<RunState> = new Set<RunState>(['COMPLETED', 'FAILED', 'CANCELLED', 'ARCHIVED']);

// Tuned for long-running workflows (hours). Raise cautiously: the worker
// batches progress on a ~5s drain interval server-side, so faster polling
// doesn't surface newer data than the worker has already posted.
const POLL_INTERVAL_MS = 10000;
// After first-seen-terminal, keep polling for two more intervals so we pick
// up the worker's trailing drain (the final {percent:100} can land AFTER
// the task/workflow transitions to terminal).
const TERMINAL_GRACE_POLLS = 2;

export default function useAutofetchedWorkflowProgressAsync(runId: string): IAsync<IWorkflowProgress> {
  const async = useStore(
    useCallback((state: State) => getCurrentProjectState(state).workflowProgressAsyncMap[runId], [runId]),
  );

  const workflowState = async?.data?.workflow_state;
  const isTerminal = workflowState !== undefined && TERMINAL_STATES.has(workflowState);

  useEffect(() => {
    fetchWorkflowProgress(runId);
    if (isTerminal) {
      let remaining = TERMINAL_GRACE_POLLS;
      const intervalId = setInterval(() => {
        fetchWorkflowProgress(runId);
        remaining -= 1;
        if (remaining <= 0) clearInterval(intervalId);
      }, POLL_INTERVAL_MS);
      return () => clearInterval(intervalId);
    }
    const intervalId = setInterval(() => fetchWorkflowProgress(runId), POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [runId, isTerminal]);

  return async || initAsync;
}
